import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable , inject} from '@angular/core';
import { Location } from '@app/common/location-editor/location-editor.component';
import { Address } from '@app/shared/address/address';
import { ReferenceTypeCode } from '@app/shared/reference-types/reference-type';
import { isEmptyDeep, toIsoDateString } from '@app/shared/utilities/toolbox';
import { isEmpty } from 'lodash';
import { Observable } from 'rxjs';

export interface PurchaseOrderAddress {
	line1?: string;
	line2?: string;
	line3?: string;
	city?: string;
	stateAbbreviation?: string;
	postalCode?: string;
	countryAbbreviation: string;
}

export interface PurchaseOrderContact {
	name: string;
	companyName?: string;
	phone?: string;
	email?: string;
	address?: PurchaseOrderAddress;
}

export interface PurchaseOrderLineItem {
	lineNumber: number;
	description: string;
	quantity?: number;
	dueDate?: string;
	unitPrice?: number;
	totalPrice?: number;
	partNumber?: string;
}

export interface PurchaseOrderShipmentReferenceNumber {
	number: string;
	type: ReferenceTypeCode;
	source: string;
}

export interface PurchaseOrderShipmentProgressionEvent {
	comments: string;
	createdAt: string;
	occurredAt: string;
	eventType: {
		code: string;
		description: string;
	};
	lastKnownLocation: Address;
}

export interface PurchaseOrderShipment {
	identifier: string;
	status: string;
	billOfLadingIdentifier?: string;
	latestProgressionEvent?: PurchaseOrderShipmentProgressionEvent;
	referenceNumbers?: PurchaseOrderShipmentReferenceNumber[];
}

export type PurchaseOrderStatus = 'Open' | 'Closed';

export interface ShippingPreferences {
	carrierAccountCode: string;
	carrierCode: string;
}

export interface PurchaseOrder {
	identifier: string;
	customerCode: string;
	divisionIdentifier: string;
	number: string;
	issuedDate: string;
	closedDate?: string;
	status: PurchaseOrderStatus;
	accessCode: string;
	originator?: PurchaseOrderContact;
	freightBillTo?: PurchaseOrderContact;
	supplier?: PurchaseOrderContact;
	shipTo?: PurchaseOrderContact;
	authorizedBy?: PurchaseOrderContact;
	shippingContact?: PurchaseOrderContact;
	shippingInstructions?: string;
	lineItems?: PurchaseOrderLineItem[];
	shippingPreferences?: ShippingPreferences;
	shipments?: PurchaseOrderShipment[];
}

export interface PurchaseOrderPatch {
	originator?: PurchaseOrderContact;
	freightBillTo?: PurchaseOrderContact;
	shipTo?: PurchaseOrderContact;
	shippingContact?: PurchaseOrderContact;
	shippingInstructions?: string;
	lineItems?: PurchaseOrderLineItem[];
	shippingPreferences?: {
		carrierAccountCode: string;
		carrierCode: string;
	};
}

type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

export type PurchaseOrderRequest = Optional<
	Omit<PurchaseOrder, 'identifier' | 'closedDate' | 'status' | 'accessCode'>,
	'number' | 'issuedDate'>;

export function locationFromContact(contact: PurchaseOrderContact): Location {
	if (!contact) {
		return null;
	}
	
	return {
		companyName: contact.companyName,
		address: contact.address,
		contact: {
			name: contact.name,
			email: contact.email,
			phone: {
				number: contact.phone
			}
		}
	}
}

export function contactFromLocation(location: Location): PurchaseOrderContact {
	if (!location) {
		return null;
	}

	return {
		name: location.contact?.name,
		companyName: location.companyName,
		email: location.contact?.email,
		phone: location.contact?.phone?.number,
		address: isEmptyDeep(location.address) ? null : {
			line1: location.address?.line1,
			line2: location.address?.line2,
			line3: location.address?.line3,
			city: location.address?.city,
			stateAbbreviation: location.address?.stateAbbreviation,
			postalCode: location.address?.postalCode,
			countryAbbreviation: location.address?.countryAbbreviation
		}
	}
}

@Injectable({
	providedIn: 'root'
})
export class PurchaseOrdersService {
	private http = inject(HttpClient);




	getPurchaseOrders(customerCode?: string, status?: PurchaseOrderStatus, startIndex?: number, pageSize?: number, divisionIdentifier?: string, supplierName?: string, issuedDateStart?: Date, issuedDateEnd?: Date, number?: string) : Observable<PurchaseOrder[]> {
		let params = new HttpParams().set('api-version', '1.0');
		params = params.set('startIndex', startIndex ?? 0);
		params = params.set('pageSize', pageSize ?? 25);

		if (!isEmpty(customerCode)) {
			params = params.set('customerCode', customerCode);
		}

		if (!isEmpty(status)) {
			params = params.set('status', status);
		}

		if (!isEmpty(divisionIdentifier)) {
			params = params.set('divisionIdentifier', divisionIdentifier);
		}

		if (!isEmpty(supplierName)) {
			params = params.set('supplierName', supplierName);
		}

		if (issuedDateStart) {
			params = params.set('issuedDateStart', toIsoDateString(issuedDateStart));
		}

		if (issuedDateEnd) {
			params = params.set('issuedDateEnd', toIsoDateString(issuedDateEnd));
		}

		if (!isEmpty(number)) {
			params = params.set('number', number);
		}

		return this.http.get<PurchaseOrder[]>(
			'/api/purchaseOrders/purchaseOrders',
			{ params }
		);
	}

	createPurchaseOrder(request: PurchaseOrderRequest): Observable<PurchaseOrder> {
		return this.http.post<PurchaseOrder>('/api/purchaseOrders/purchaseOrders?api-version=1.0', request);
	}

	getPurchaseOrder(purchaseOrderId: string): Observable<PurchaseOrder> {
		return this.http.get<PurchaseOrder>(`/api/purchaseOrders/purchaseOrders/${purchaseOrderId}?api-version=1.0`);
	}

	emailPurchaseOrder(purchaseOrderId: string, recipients: {recipients: string[]}) {
		return this.http.post(`/api/purchaseOrders/purchaseOrders/${purchaseOrderId}/send?api-version=1.0`, recipients);
	}

	closePurchaseOrder(purchaseOrderId: string): Observable<PurchaseOrder> {
		return this.http.post<PurchaseOrder>(`/api/purchaseOrders/purchaseOrders/${purchaseOrderId}/close?api-version=1.0`, {});
	}

	reopenPurchaseOrder(purchaseOrderId: string): Observable<PurchaseOrder> {
		return this.http.post<PurchaseOrder>(`/api/purchaseOrders/purchaseOrders/${purchaseOrderId}/reopen?api-version=1.0`, {});
	}

	updatePurchaseOrder(purchaseOrderId: string, patch: Partial<PurchaseOrderPatch>) {
		return this.http.patch<PurchaseOrder>(`/api/purchaseOrders/purchaseOrders/${purchaseOrderId}?api-version=1.0`, patch);
	}
}
